import type { IModuleSpec } from '~/types/designRequest';
import type { ICatalogPanel } from '~/types/equipment';

/**
 * The panel of Auto-Approval's Default Design Config (auto-approval-service's `AVERAGE_DEFAULT_CONFIG`, the
 * `moduleSpec` it sends Scanifly today), with its equipment catalog uuid. The electrical figures are the ones the
 * service's own tests use.
 */
export const DEFAULT_PANEL: ICatalogPanel = {
    uuid: 'f6d6a406-db84-459b-9764-12dfe622c08f',
    manufacturer_name: 'REC Group',
    series: 'Alpha Pure-RX',
    model_number: 'REC460AA Pure-RX Protrust',
    rated_power: 460,
    efficiency: 22.1,
    length: 1727.2,
    width: 1203.96,
    open_circuit_voltage_voc: 49.9,
    optimum_operating_voltage_vmp: 42.3,
    temperature_coefficient: -0.24,
};

/** As the equipment site titles a card: "<series> <model_number>", where there is a series. */
export const panelTitle = (panel: { series?: string | null; model_number?: string | null }) =>
    [panel.series, panel.model_number].filter(Boolean).join(' ') || 'Unnamed panel';

const known = (value: number | null | undefined): value is number => value != null && Number.isFinite(value);

/** The figures a panel card shows, as on the equipment site, with the size a Design lays out. */
export const panelSpecs = (
    panel: Pick<IModuleSpec, 'rated_power' | 'efficiency' | 'length' | 'width'> | ICatalogPanel,
): { label: string; value: string }[] => [
    ...(known(panel.rated_power) ? [{ label: 'Rated Power', value: `${panel.rated_power}W` }] : []),
    ...(known(panel.efficiency) ? [{ label: 'Efficiency', value: `${panel.efficiency}%` }] : []),
    ...(known(panel.length) && known(panel.width)
        ? [{ label: 'Size', value: `${Math.round(panel.length)} × ${Math.round(panel.width)} mm` }]
        : []),
];

const positive = (value: number | null | undefined) => value != null && value > 0;

/** A Design needs a panel's power and size: a catalog record without them can't be laid out. */
export const isDesignable = (panel: ICatalogPanel) =>
    positive(panel.rated_power) && positive(panel.length) && positive(panel.width);

/** The request's module: the catalog record under its own field names, as a caller sends it. */
export const toModuleSpec = (panel: ICatalogPanel): IModuleSpec => ({
    uuid: panel.uuid,
    manufacturer_name: panel.manufacturer_name,
    series: panel.series ?? null,
    model_number: panel.model_number,
    rated_power: panel.rated_power ?? Number.NaN,
    efficiency: panel.efficiency ?? null,
    length: panel.length ?? Number.NaN,
    width: panel.width ?? Number.NaN,
    open_circuit_voltage_voc: panel.open_circuit_voltage_voc ?? null,
    optimum_operating_voltage_vmp: panel.optimum_operating_voltage_vmp ?? null,
    temperature_coefficient: panel.temperature_coefficient ?? null,
    temp_coeff_pmax: panel.temp_coeff_pmax ?? null,
});
